import axios from "axios";

export default class MitNavigationEngine {
    
    /**
     * Calls the PostGIS routing backend to calculate the shortest path.
     * The database automatically resolves the centroids and building IDs.
     * * @param {string} startMitId - The mit_id of the starting space/object
     * @param {string} endMitId - The mit_id of the destination space/object
     * @param {string} routingOption - 'fastest', 'avoid_stairs', 'avoid_elevators'
     * @returns {Promise<Object>} GeoJSON FeatureCollection of the route
     */
    async getRoute(startMitId, endMitId, routingOption = 'fastest') {
        console.group("[NavEngine] 🚀 REQUESTING ROUTE FROM SERVER");
        console.log(`Start ID: ${startMitId} | Dest ID: ${endMitId} | Option: ${routingOption}`);
        
        try {
            const response = await axios.get('/api/v2/user/spaces/navigation/route', {
                params: {
                    startMitId: startMitId,
                    endMitId: endMitId,
                    routingOption: routingOption
                }
            });
            
            console.log(`✅ Route found! Total features returned: ${response.data.features.length}`);
            console.groupEnd();
            
            return response.data; 
        } catch (error) {
            console.error("🚨 Routing API failed:", error);
            console.groupEnd();
            throw error;
        }
    }

    /**
     * Groups the backend route features into floor-by-floor segments for the UI sidebar.
     * * @param {Object} routeData - The GeoJSON FeatureCollection from getRoute()
     * @returns {Array} Array of instruction segments grouped by floor level
     */
    generateFloorInstructions(routeData) {
        let instructions = [];
        
        if (!routeData || !routeData.features || routeData.features.length === 0) {
            return instructions;
        }

        let features = routeData.features;
        
        // Find the starting floor from the very first feature that has a level property
        let currentFloor = null;
        for (let f of features) {
            if (f.properties && f.properties.level !== undefined) {
                currentFloor = f.properties.level;
                break;
            }
        }

        // Failsafe if the data is malformed
        if (currentFloor === null) return instructions;

        let currentSegment = { floor: currentFloor, routeFeatures: [] };

        features.forEach(f => {
            // If the feature explicitly states a different level, we've transitioned floors (via elevator/stairs)
            if (f.properties && f.properties.level !== undefined && String(f.properties.level) !== String(currentFloor)) {
                
                // Push the completed segment
                instructions.push(currentSegment);
                
                // Start a new segment for the new floor
                currentFloor = f.properties.level;
                currentSegment = { floor: currentFloor, routeFeatures: [] };
            }
            
            currentSegment.routeFeatures.push(f);
        });

        // Push the final segment
        if (currentSegment.routeFeatures.length > 0) {
            instructions.push(currentSegment);
        }

        return instructions;
    }
}